import { useCallback, useMemo, useState } from 'react';
import type { ArtifactKind } from '@inference.engine/widgets';
import type { ProjectContextItem, ProjectContextSelection } from '@/features/chats/stores';
import { nextAttachmentOrder } from '../../lib/attachmentOrder';
import { rebuildProjectContextSelection } from '../projectContextSelection.utils';
import type {
  UseChatProjectContextPickerProps,
  UseChatProjectContextPickerResult,
} from './useChatProjectContextPicker.types';

/**
 * Owns the project-context picker for the chat composer: the stored-selection
 * narrowing, the fully-controlled picker modal state, and the add / remove /
 * confirm handlers. Lifted out of `ChatInput` so the composer just renders
 * chips, a badge count, and a controlled `<ProjectContextPicker>`.
 *
 * The rows hook is injected (not imported) so `features/chats` stays free of
 * any `@/features/add-project-context` dependency.
 */
export const useChatProjectContextPicker = (
  props: UseChatProjectContextPickerProps,
): UseChatProjectContextPickerResult => {
  const { options, updateOptions, useProjectContextRows } = props;

  // Stored project-context selection (mirrors the knowledgeBaseIds options
  // pattern). Narrowed from the untyped options bag at read time.
  const projectContext = options?.projectContext as ProjectContextSelection | undefined;
  const projectContextItems = useMemo(() => projectContext?.items ?? [], [projectContext]);
  const projectContextCount = projectContextItems.length;

  // Picker modal state. The picker is fully controlled, so the composer owns
  // every piece of its toolbar + selection state.
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [pickerProjectId, setPickerProjectId] = useState<string | null>(null);
  const [pickerProjectSearch, setPickerProjectSearch] = useState('');
  const [pickerSelectedIds, setPickerSelectedIds] = useState<Set<string>>(() => new Set());
  const [pickerKindFilter, setPickerKindFilter] = useState<ArtifactKind | null>(null);
  const [pickerSort, setPickerSort] = useState<string | null>(null);
  const [pickerSearch, setPickerSearch] = useState('');

  // Host loads the same rows the picker renders so it can resolve the selected
  // ids into chip items (name + kind) on confirm. React Query dedups this with
  // the picker's own call.
  const { rows: pickerRows } = useProjectContextRows(pickerProjectId ?? undefined);

  const handleAddProjectContext = useCallback(() => {
    // Seed the picker from the stored selection so re-opening preserves the
    // prior pick.
    setPickerProjectId(projectContext?.projectId ?? null);
    setPickerSelectedIds(new Set(projectContext?.items.map((item) => item.id) ?? []));
    setPickerProjectSearch('');
    setPickerKindFilter(null);
    setPickerSort(null);
    setPickerSearch('');
    setIsPickerOpen(true);
  }, [projectContext]);

  // Changing the project clears the in-flight selection — ids from one project
  // don't carry over to another.
  const handlePickerProjectIdChange = useCallback((next: string | null) => {
    setPickerProjectId(next);
    setPickerSelectedIds(new Set());
  }, []);

  const handleConfirmProjectContext = useCallback(() => {
    if (!pickerProjectId) return;

    const selectedRows = pickerRows.filter((row) => pickerSelectedIds.has(row.id));
    // Preserve the insertion order of already-selected items; assign a fresh one
    // to newly-selected items so they append after existing attachments.
    const priorById = new Map(projectContext?.items.map((item) => [item.id, item]) ?? []);
    const items: ProjectContextItem[] = selectedRows.map((row) => ({
      id: row.id,
      name: row.name,
      kind: row.kind,
      // Image rows carry a signed thumbnail; keep it so the composer renders the
      // image as a thumbnail chip rather than a generic kind icon.
      thumbnailUrl: row.thumbnailUrl ?? undefined,
      addedAt: priorById.get(row.id)?.addedAt ?? nextAttachmentOrder(),
    }));

    // Briefs ride the wire with their content inline (the inference-api must not
    // call workspace-api). Each selected brief row carries the TipTap `sections`
    // the rows hook retained; artifacts stay id-only.
    const briefs = selectedRows
      .filter((row) => row.kind === 'brief')
      .map((row) => ({
        id: row.id,
        title: row.name,
        sections: row.sections ?? [],
      }));

    const selection: ProjectContextSelection = {
      projectId: pickerProjectId,
      briefs,
      artifactIds: items.filter((item) => item.kind !== 'brief').map((item) => item.id),
      items,
    };

    updateOptions({
      ...(options ?? {}),
      projectContext: items.length > 0 ? selection : undefined,
    });
    setIsPickerOpen(false);
  }, [options, pickerProjectId, pickerRows, pickerSelectedIds, projectContext, updateOptions]);

  const handleRemoveProjectContextItem = useCallback(
    (id: string) => {
      if (!projectContext) return;

      const nextSelection = rebuildProjectContextSelection(
        projectContext,
        projectContext.items.filter((item) => item.id !== id),
      );

      updateOptions({ ...(options ?? {}), projectContext: nextSelection });
    },
    [options, projectContext, updateOptions],
  );

  const pickerProps = useMemo(
    () => ({
      selectedProjectId: pickerProjectId,
      onSelectedProjectIdChange: handlePickerProjectIdChange,
      projectSearchInput: pickerProjectSearch,
      onProjectSearchInputChange: setPickerProjectSearch,
      selectedIds: pickerSelectedIds,
      onSelectedIdsChange: setPickerSelectedIds,
      kindFilter: pickerKindFilter,
      onKindFilterChange: setPickerKindFilter,
      sort: pickerSort,
      onSortChange: setPickerSort,
      searchInput: pickerSearch,
      onSearchInputChange: setPickerSearch,
    }),
    [
      pickerProjectId,
      handlePickerProjectIdChange,
      pickerProjectSearch,
      pickerSelectedIds,
      pickerKindFilter,
      pickerSort,
      pickerSearch,
    ],
  );

  return {
    projectContextItems,
    projectContextCount,
    isPickerOpen,
    setIsPickerOpen,
    handleAddProjectContext,
    handleRemoveProjectContextItem,
    handleConfirmProjectContext,
    canConfirm: !!pickerProjectId,
    selectedCount: pickerSelectedIds.size,
    pickerProps,
  };
};
